import React, { useState } from 'react';
import { useToast } from '../../Toast';

interface ExportRecord {
  id: string;
  created: string;
  fileName: string;
  exportedBy: string;
  password: string;
  status: 'ready' | 'processing';
  size: string;
}

const DataExport: React.FC = () => {
  const toast = useToast();
  const [exports, setExports] = useState<ExportRecord[]>([
    {
      id: '1',
      created: 'October 16, 2025\n7:19 PM',
      fileName: 'MyClinicCore 2025 annual financial activity report for The Real Therapists of Atlanta',
      exportedBy: 'Tara Miller',
      password: 'Not protected',
      status: 'ready',
      size: '695 B'
    }
  ]);

  const handleStartExport = () => {
    const newExport: ExportRecord = {
      id: Date.now().toString(),
      created: new Date().toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).replace(', ', '\n'),
      fileName: 'Practice data export ' + new Date().toLocaleDateString(),
      exportedBy: 'Tara Miller',
      password: 'Not protected',
      status: 'processing' as const,
      size: 'Processing...'
    };

    setExports([newExport, ...exports]);

    // Simulate export completion
    setTimeout(() => {
      setExports(prev => prev.map(exp =>
        exp.id === newExport.id
          ? { ...exp, status: 'ready' as const, size: '1.2 MB' }
          : exp
      ));
    }, 3000);
  };

  const handleDownload = (exportId: string) => {
    console.log('Downloading export:', exportId);
    toast.success('Export Started', 'Your data export download has started.');
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1400px' }}>
      {/* Header with Start Export Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Data export
          </h1>
          <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
            Export practice data
          </p>
        </div>
        <button
          onClick={handleStartExport}
          style={{
            padding: '10px 24px',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          Start export
        </button>
      </div>

      {/* Recent Exports */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 16px 0' }}>
          Recent exports
        </h2>

        {exports.length > 0 ? (
          <div style={{
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#0A2540' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>
                    Created
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>
                    File name
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>
                    Exported by
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>
                    Password
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {exports.map((exp, index) => (
                  <tr key={exp.id} style={{ borderTop: index > 0 ? '1px solid #6CA6C1' : 'none' }}>
                    <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px', whiteSpace: 'pre-line' }}>
                      {exp.created}
                    </td>
                    <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>
                      {exp.fileName}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#0A2540',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}>
                        <span style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#6CA6C1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 600,
                          color: '#0A2540'
                        }}>
                          You
                        </span>
                        <span style={{ color: '#FFFFFF', fontSize: '13px' }}>{exp.exportedBy}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px' }}>
                      {exp.password}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {exp.status === 'ready' ? (
                        <button
                          onClick={() => handleDownload(exp.id)}
                          style={{
                            padding: '6px 16px',
                            backgroundColor: '#226F9E',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>📥</span>
                          Ready for download ({exp.size})
                        </button>
                      ) : (
                        <span style={{
                          padding: '6px 16px',
                          backgroundColor: '#0A2540',
                          color: '#87CEEB',
                          borderRadius: '4px',
                          fontSize: '12px',
                          display: 'inline-block'
                        }}>
                          Processing...
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{
              padding: '16px',
              borderTop: '1px solid #6CA6C1',
              backgroundColor: '#0A2540',
              fontSize: '13px',
              color: '#87CEEB'
            }}>
              Exports will permanently expire after a week.
            </div>
          </div>
        ) : (
          <div style={{
            padding: '48px',
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#87CEEB', fontSize: '14px' }}>
              No exports yet. Click "Start export" to create your first export.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataExport;
